import { describeDatasetSettings } from "constants/marketPriceSetting";
import dayjs from "dayjs";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetMarketPriceDatasets,
  useDeleteMarketPriceDataset,
  useGetMarketPriceDatasetDownloadUrl,
} from "store/queries/marketPrices";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { IMarketPriceDataset } from "types/marketPrice";

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${sizer(3)};
  gap: ${sizer(2)};
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
  .p-button-label {
    font-weight: 600;
  }
`;

const StyledLink = styled.span`
  color: #2196f3;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export function MarketPricesTable() {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const {
    data: datasets,
    isLoading,
    refetch,
  } = useGetMarketPriceDatasets({
    search: globalFilter,
  });
  const [deleteDataset] = useDeleteMarketPriceDataset();
  const [getDownloadUrl] = useGetMarketPriceDatasetDownloadUrl();

  const handleDelete = useCallback(
    (dataset: IMarketPriceDataset) => {
      confirmDialog({
        message: `Are you sure you want to delete "${dataset.displayName}"? This will permanently remove all market prices associated with this dataset.`,
        header: "Confirm Delete",
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        accept: async () => {
          try {
            await deleteDataset(dataset.id).unwrap();
            toast?.current?.show({
              severity: "success",
              summary: "Success",
              detail: "Market price dataset deleted successfully",
            });
            refetch();
          } catch (error: any) {
            toast?.current?.show({
              severity: "error",
              summary: "Error",
              detail: error?.data?.message || "Failed to delete dataset",
            });
          }
        },
      });
    },
    [deleteDataset, refetch]
  );

  const handleDownload = useCallback(
    async (dataset: IMarketPriceDataset) => {
      try {
        const response = await getDownloadUrl(dataset.id).unwrap();
        if (response.url) {
          window.open(response.url, "_blank");
        }
      } catch (error: any) {
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.data?.message || "Failed to get download URL",
        });
      }
    },
    [getDownloadUrl]
  );

  const handleView = useCallback(
    (dataset: IMarketPriceDataset) => {
      navigate(`/payer-rules/market-prices/${dataset.id}`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (dataset: IMarketPriceDataset) => {
      navigate(`/payer-rules/market-prices/edit/${dataset.id}`);
    },
    [navigate]
  );

  const actionBodyTemplate = (rowData: IMarketPriceDataset) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleEdit(rowData)}
        />
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleDownload(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
        />
      </>
    );
  };

  const uploadedBodyTemplate = (rowData: IMarketPriceDataset) => {
    const date = dayjs(rowData.createdAt).format("MMM DD, YYYY");
    const uploadedBy = rowData.uploadedBy?.name || "Unknown";
    return `${date} (by ${uploadedBy})`;
  };

  // CLE-2939: one line per setting, e.g. "Hospital (37,784)".
  const settingsBodyTemplate = (rowData: IMarketPriceDataset) => {
    const settings = describeDatasetSettings(rowData.metadata);
    if (settings.length === 0) return "—";
    return (
      <div className="flex flex-column gap-1">
        {settings.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    );
  };

  const countBodyTemplate = (rowData: IMarketPriceDataset) => {
    // eslint-disable-next-line no-underscore-dangle
    return rowData._count?.marketPrices || 0;
  };

  const { data: providers } = useGetAllProvidersQuery();

  const displayNameBodyTemplate = (rowData: IMarketPriceDataset) => {
    return (
      <StyledLink onClick={() => handleView(rowData)}>
        {rowData.displayName}
      </StyledLink>
    );
  };

  const providersBodyTemplate = (rowData: IMarketPriceDataset) => {
    if (!rowData.providerIds || !providers?.data) {
      return "N/A";
    }

    const providerNames = rowData.providerIds
      .map((id) => providers.data.find((p: any) => p.id === id)?.name)
      .filter(Boolean);

    if (providerNames.length === 0) return "N/A";
    if (providerNames.length === 1) return providerNames[0];
    if (providerNames.length <= 3) return providerNames.join(", ");

    return `${providerNames.slice(0, 2).join(", ")} +${
      providerNames.length - 2
    } more`;
  };

  const header = (
    <FlexWrapper>
      <SearchWrapper className="p-inputgroup">
        <span className="p-inputgroup-addon">
          <i className="pi pi-search" />
        </span>
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search datasets..."
        />
      </SearchWrapper>
      <div className="flex gap-2">
        <Button
          label="View All Prices"
          icon="pi pi-list"
          className="p-button-outlined"
          onClick={() => navigate("/payer-rules/market-prices")}
        />
        <StyledButton
          icon="pi pi-upload"
          className="p-button-raised gap-2"
          onClick={() => navigate("/payer-rules/market-prices/upload")}
        >
          {" "}
          Upload
        </StyledButton>
      </div>
    </FlexWrapper>
  );

  if (isLoading) {
    return (
      <div className="flex justify-content-center">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <DataTable
        value={datasets || []}
        header={header}
        loading={isLoading}
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50]}
        emptyMessage="No market price datasets found"
        className="datatable-responsive"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} datasets"
        rowHover
        stripedRows
      >
        <Column
          field="displayName"
          header="Name"
          body={displayNameBodyTemplate}
          sortable
        />
        <Column
          header="Provider(s)"
          body={providersBodyTemplate}
          style={{ minWidth: "200px" }}
        />
        <Column
          field="createdAt"
          header="Uploaded"
          body={uploadedBodyTemplate}
          sortable
          style={{ minWidth: "250px" }}
        />
        <Column
          header="Setting(s)"
          body={settingsBodyTemplate}
          style={{ minWidth: "200px" }}
        />
        <Column
          field="_count.marketPrices"
          header="Price Count"
          body={countBodyTemplate}
          sortable
        />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ width: "180px" }}
        />
      </DataTable>
    </>
  );
}

export default MarketPricesTable;
